import { Component, OnInit, Input } from '@angular/core';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { PageEvent } from '@angular/material/paginator';
import { ProfileService } from 'app/profile/services/profile.service';
import { ToolParameterType, ToolSubcategory, ToolCategory, ToolDeliverableType } from 'app/profile/models/generic.model';
import { SelectToolCategories, SelectToolSubCategories } from 'app/profile/store/app.selector';
import { GeneralSettingsState } from 'app/profile/store/state/general.state';
import { Store } from '@ngrx/store';
import { TslDialog } from '@tesla/ng-tds';
import { I18nService } from 'app/shared/services/i18n.service';

@Component({
  selector: 'app-defaults-approval-grid',
  templateUrl: './defaults-approval-grid.component.html',
  styleUrls: ['./defaults-approval-grid.component.scss']
})
export class DefaultsApprovalGridComponent implements OnInit {
  @Input() supplierCode: string;
  datasource: BehaviorSubject<any> = new BehaviorSubject([]);

  filterCategories: ToolCategory[];
  allSubcategories: ToolSubcategory[];
  pendingDefaultsList: any[] = [];
  actionItemsList: any[] = [];

  parameters: ToolParameterType[] = [];
  deliverables: ToolDeliverableType[] = [];
  displayedColumns = ['category', 'select', 'type', 'eventType', 'description'];
  pageSizeOptions: number[] = [10, 25, 50, 100, 250];
  pageIndex = 0;
  pageSize = 10;
  total = 0;
  constructor(private service: ProfileService,
    private store: Store<GeneralSettingsState>,
    public translate: I18nService,
    private dialog: TslDialog) { }

  ngOnInit() {
    this.loadResources();
  }

  loadResources() {
    combineLatest(
      [this.store.select(SelectToolCategories),
      this.store.select(SelectToolSubCategories)])
      .subscribe(([categories, subCategories]) => {
        if (categories && subCategories) {
          this.filterCategories = categories.sort((a, b) => a.category.trim().toLowerCase().localeCompare(b.category.trim().toLowerCase(), undefined, { sensitivity: 'base' }));;
          this.allSubcategories = subCategories[this.supplierCode].sort((a, b) => a.subcategory.trim().toLowerCase().localeCompare(b.subcategory.trim().toLowerCase(), undefined, { sensitivity: 'base' }));
        }
      });
    combineLatest(
      [this.service.getPendingDefaultParameters(),
      this.service.getPendingDefaultDeliverables()]).subscribe(([params, del]) => {
        this.parameters = params;
        this.deliverables = del;
        this.loadTableData();
      })
  }

  loadTableData() {
    this.pendingDefaultsList = [];
    if (this.parameters && this.parameters.length > 0) {
      this.pendingDefaultsList = this.pendingDefaultsList.concat(this.parameters.map(x => {
        return {
          subCategoryId: x.toolSubcategoryId,
          paramId: x.id,
          name: x.type,
          type: 'Parameter',
          eventType: null,
          subCategory: this.allSubcategories.filter(sc => sc.id == x.toolSubcategoryId)[0].subcategory,
          category: this.getCategoryName(x.toolSubcategoryId)
        }
      }));
    }
    if (this.deliverables && this.deliverables.length > 0) {
      this.pendingDefaultsList = this.pendingDefaultsList.concat(this.deliverables.map(x => {
        return {
          subCategoryId: x.toolSubcategoryId,
          deliverableId: x.id,
          name: x.deliverable,
          type: 'Deliverable',
          eventType: x.eventType,
          subCategory: this.allSubcategories.filter(sc => sc.id == x.toolSubcategoryId)[0].subcategory,
          category: this.getCategoryName(x.toolSubcategoryId)
        }
      }));
    }
    this.loadGrid();
  }

  getCategoryName(Id: number): string {
    let categoryId = this.allSubcategories.filter(sc => sc.id == Id)[0].toolCategoryId;
    let category = this.filterCategories.filter(c => c.id == categoryId)[0].category;
    return category;
  }

  loadGrid() {
    this.total = this.pendingDefaultsList.length;
    const skip = this.pageIndex * this.pageSize;
    const take = (this.pageIndex + 1) * this.pageSize;
    this.datasource.next(this.pendingDefaultsList.slice(skip, take));
  }

  onPageChange(pageChange: PageEvent): void {
    const { pageIndex, pageSize } = pageChange;
    this.pageIndex = pageIndex;
    this.pageSize = pageSize;
    this.loadGrid();
  }

  isHeader(index, item): boolean {
    // returns if row item is a header
    return item.isGroup;
  }

  isSelected(checked: any, row: any) {
    if (checked) {
      this.actionItemsList.push(row);
    }
    else {
      const index = this.actionItemsList.indexOf(row);
      if (index >= 0) {
        this.actionItemsList.splice(index, 1);
      }
    }
  }

  ApproveDefaults() {
    if (this.actionItemsList && this.actionItemsList.length > 0) {
      this.dialog.confirm(this.translate.get('Confirm Submission'), this.translate.get('Are you sure you want to approve the selected defaults?'), this.translate.get('Yes'), this.translate.get('No')).afterClosed().subscribe((x: boolean) => {
        if (x) {
          // seperate parameters and deliverables
          var params = this.actionItemsList.filter(p => p.type === 'Parameter');
          var approvedParams: ToolParameterType[] = [];
          if (params && params.length > 0) {
            params.forEach(x => {
              var item = new ToolParameterType();
              item.id = x.paramId;
              item.supplierCode = null;
              item.toolSubcategoryId = x.subCategoryId;
              item.type = x.name;
              item.status = 'Approved';
              item.isDefault = true;
              approvedParams.push(item);
            });

            //Update 
            this.service.approveOrRejectDefaultParameter(approvedParams).subscribe(res => {
              if (res && res.length > 0) {
                this.actionItemsList = [];
                this.service.getPendingDefaultParameters().subscribe(x => {
                  this.parameters = x;
                  this.loadTableData();
                });
              }
            });
          }

          var del = this.actionItemsList.filter(d => d.type === 'Deliverable');
          var approvedDel: ToolDeliverableType[] = [];
          if (del && del.length > 0) {
            del.forEach(x => {
              var item = new ToolDeliverableType();
              item.id = x.deliverableId;
              item.toolSubcategoryId = x.subCategoryId;
              item.eventType = x.eventType;
              item.deliverable = x.name;
              item.status = 'Approved';
              item.isDefault = true;
              approvedDel.push(item);
            });

            //Update 
            this.service.approveOrRejectDefaultDeliverables(approvedDel).subscribe(res => {
              if (res && res.length > 0) {
                this.actionItemsList = [];
                this.service.getPendingDefaultDeliverables().subscribe(x => {
                  this.deliverables = x;
                  this.loadTableData();
                });
              }
            });
          }

          // add category and subcategory grouping
        }
      });
    }
  }

  RejectDefaults() {
    if (this.actionItemsList && this.actionItemsList.length > 0) {
      this.dialog.confirm(this.translate.get('Confirm Submission'), this.translate.get('Are you sure you want to reject the selected defaults?'), this.translate.get('Yes'), this.translate.get('No')).afterClosed().subscribe((x: boolean) => {
        if (x) {
          // seperate parameters and deliverables
          var params = this.actionItemsList.filter(p => p.type === 'Parameter');
          var rejectedParams: ToolParameterType[] = [];
          if (params && params.length > 0) {
            params.forEach(x => {
              var item = new ToolParameterType();
              item.id = x.paramId;
              item.supplierCode = null;
              item.toolSubcategoryId = x.subCategoryId;
              item.type = x.name;
              item.status = 'Rejected';
              item.isDefault = true;
              rejectedParams.push(item);
            });

            //Update 
            this.service.approveOrRejectDefaultParameter(rejectedParams).subscribe(res => {
              if (res && res.length > 0) {
                this.actionItemsList = [];
                this.service.getPendingDefaultParameters().subscribe(x => {
                  this.parameters = x;
                  this.loadTableData();
                });
              }
            });
          }

          var del = this.actionItemsList.filter(d => d.type === 'Deliverable');
          var rejectedDel: ToolDeliverableType[] = [];
          if (del && del.length > 0) {
            del.forEach(x => {
              var item = new ToolDeliverableType();
              item.id = x.deliverableId;
              item.toolSubcategoryId = x.subCategoryId;
              item.eventType = x.eventType;
              item.deliverable = x.name;
              item.status = 'Rejected';
              item.isDefault = true;
              rejectedDel.push(item);
            });

            //Update 
            this.service.approveOrRejectDefaultDeliverables(rejectedDel).subscribe(res => {
              if (res && res.length > 0) {
                this.actionItemsList = [];
                this.service.getPendingDefaultDeliverables().subscribe(x => {
                  this.deliverables = x;
                  this.loadTableData();
                });
              }
            });
          }
        }
      });
    }
  }
}
